/**
 * ComponentRenderers
 *
 * Renders all 29 Component Library blocks saved by the backoffice page builder.
 * Each renderer reads from `block.props` with fallback to offer data where applicable.
 *
 * Component IDs match the backoffice component-library catalog:
 *   Tier 1 (8): hero-banner, product-showcase, testimonial-section, feature-grid,
 *               pricing-table, faq-accordion, newsletter-signup, social-proof-bar
 *   Tier 2 (10): image-text-block, cta-button-group, countdown-timer, progress-bar,
 *                video-embed, icon-list, stats-counter, comparison-table,
 *                badge-ribbon, divider
 *   Tier 3 (10): heading, paragraph, image, button, spacer, container, card,
 *                input-field, checkbox, select / select-dropdown
 *   Interactive (1): spin-the-wheel
 */

import { useId, useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import type { FunnelOffer, StepPageBlock } from '../types';
import { useOptionalFunnelContext } from '../funnel-provider';
import { useOfferImage } from '../use-offer-image';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert snake_case key to camelCase for fallback lookup. */
function toCamel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

function str(block: StepPageBlock, key: string, fallback = ''): string {
  // Look up snake_case first, then camelCase (template builder uses camelCase)
  const v = block.props?.[key] ?? block.props?.[toCamel(key)];
  return typeof v === 'string' && v.trim() ? v : fallback;
}

function num(block: StepPageBlock, key: string, fallback: number): number {
  const v = block.props?.[key] ?? block.props?.[toCamel(key)];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function bool(block: StepPageBlock, key: string, fallback: boolean): boolean {
  const v = block.props?.[key] ?? block.props?.[toCamel(key)];
  return typeof v === 'boolean' ? v : fallback;
}

interface CtaButtonConfig {
  text?: string;
  url?: string;
  variant?: string;
}

function ctaButtons(block: StepPageBlock): CtaButtonConfig[] {
  const value = block.props?.buttons ?? block.props?.[toCamel('buttons')];
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is CtaButtonConfig => typeof item === 'object' && item !== null);
}

function formatPrice(value: number | null | undefined): string {
  return Number.isFinite(value) ? `$${(value as number).toFixed(2)}` : '$0.00';
}

interface ProductShowcaseItem {
  id: string;
  name: string;
  imageUrl: string | null;
  price: number | null;
  originalPrice: number | null;
}

interface PublicProductResponse {
  id?: unknown;
  title?: unknown;
  media?: unknown;
  variants?: unknown;
}

const productShowcaseCache = new Map<string, ProductShowcaseItem>();
const productShowcaseInflight = new Map<string, Promise<ProductShowcaseItem | null>>();

function getShowcaseProductImage(media: unknown): string | null {
  if (!Array.isArray(media)) return null;
  const image = media.find((entry) => {
    if (!entry || typeof entry !== 'object') return false;
    const record = entry as Record<string, unknown>;
    return typeof record.uri === 'string' && record.uri.trim() && (record.type === 'image' || record.type == null);
  });
  if (!image || typeof image !== 'object') return null;
  const uri = (image as Record<string, unknown>).uri;
  return typeof uri === 'string' && uri.trim() ? uri : null;
}

function getShowcaseVariantPricing(variants: unknown): Pick<ProductShowcaseItem, 'price' | 'originalPrice'> {
  if (!Array.isArray(variants)) {
    return { price: null, originalPrice: null };
  }

  const variant = variants.find((entry) => {
    if (!entry || typeof entry !== 'object') return false;
    const record = entry as Record<string, unknown>;
    return typeof record.price === 'number' && Number.isFinite(record.price);
  });

  if (!variant || typeof variant !== 'object') {
    return { price: null, originalPrice: null };
  }

  const record = variant as Record<string, unknown>;
  const price = typeof record.price === 'number' && Number.isFinite(record.price) ? record.price : null;
  const originalPrice =
    typeof record.compare_at_price === 'number' &&
    Number.isFinite(record.compare_at_price) &&
    record.compare_at_price !== price
      ? record.compare_at_price
      : null;

  return { price, originalPrice };
}

function mapPublicProductToShowcaseItem(raw: PublicProductResponse, fallbackId: string): ProductShowcaseItem {
  const { price, originalPrice } = getShowcaseVariantPricing(raw.variants);
  return {
    id: typeof raw.id === 'string' && raw.id.trim() ? raw.id : fallbackId,
    name: typeof raw.title === 'string' && raw.title.trim() ? raw.title : 'Product',
    imageUrl: getShowcaseProductImage(raw.media),
    price,
    originalPrice,
  };
}

async function fetchShowcaseProduct(
  apiBaseUrl: string,
  tenantId: string,
  productId: string,
): Promise<ProductShowcaseItem | null> {
  const cacheKey = `${apiBaseUrl}::${tenantId}::${productId}`;
  const cached = productShowcaseCache.get(cacheKey);
  if (cached) return cached;

  const inflight = productShowcaseInflight.get(cacheKey);
  if (inflight) return inflight;

  const request = fetch(
    `${apiBaseUrl.replace(/\/$/, '').replace(/\/v1.*$/, '')}/v1/products/${encodeURIComponent(productId)}`,
    { method: 'GET' },
  )
    .then(async (response) => {
      if (!response.ok) return null;
      const raw = (await response.json()) as PublicProductResponse;
      const mapped = mapPublicProductToShowcaseItem(raw, productId);
      productShowcaseCache.set(cacheKey, mapped);
      return mapped;
    })
    .catch(() => null)
    .finally(() => {
      productShowcaseInflight.delete(cacheKey);
    });

  productShowcaseInflight.set(cacheKey, request);
  return request;
}

function getCachedShowcaseProducts(
  apiBaseUrl: string | undefined,
  tenantId: string | undefined,
  productIds: string[],
): ProductShowcaseItem[] {
  if (!apiBaseUrl || !tenantId || !productIds.length) {
    return [];
  }

  return productIds
    .map((productId) => productShowcaseCache.get(`${apiBaseUrl}::${tenantId}::${productId}`) ?? null)
    .filter((item): item is ProductShowcaseItem => item !== null);
}

function useProductShowcase(productIds: string[] | undefined): {
  products: ProductShowcaseItem[];
  loading: boolean;
} {
  const funnelContext = useOptionalFunnelContext();
  const config = funnelContext?.config;
  const apiBaseUrl = config?.apiBaseUrl;
  const tenantId = config?.tenantId;
  const normalizedIds = useMemo(
    () =>
      Array.isArray(productIds)
        ? productIds.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
        : [],
    [productIds],
  );
  const normalizedIdsKey = normalizedIds.join('|');
  const [products, setProducts] = useState<ProductShowcaseItem[]>(() =>
    getCachedShowcaseProducts(apiBaseUrl, tenantId, normalizedIds),
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!normalizedIds.length || !apiBaseUrl || !tenantId) {
      setProducts([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const cachedProducts = getCachedShowcaseProducts(apiBaseUrl, tenantId, normalizedIds);
    const allProductsCached = cachedProducts.length === normalizedIds.length;

    if (cachedProducts.length) {
      setProducts(cachedProducts);
    }

    if (allProductsCached) {
      setLoading(false);
      return;
    }

    if (!cachedProducts.length) {
      setLoading(true);
    }

    void Promise.allSettled(
      normalizedIds.map((productId) => fetchShowcaseProduct(apiBaseUrl, tenantId, productId)),
    ).then((results) => {
      if (cancelled) return;
      setProducts(
        results
          .filter((result): result is PromiseFulfilledResult<ProductShowcaseItem | null> => result.status === 'fulfilled')
          .map((result) => result.value)
          .filter((item): item is ProductShowcaseItem => item !== null),
      );
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, tenantId, normalizedIdsKey]);

  return { products, loading };
}

// ---------------------------------------------------------------------------
// Shared Styled Primitives
// ---------------------------------------------------------------------------

const SectionTitle = styled.h2`
  margin: 0 0 16px;
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const BodyText = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

// ---------------------------------------------------------------------------
// Tier 1: Ready-to-Use Sections
// ---------------------------------------------------------------------------

const HeroBannerWrap = styled.div<{ bgColor: string; textColor: string; bgImage?: string }>`
  position: relative;
  width: 100%;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 24px;
  box-sizing: border-box;
  background-color: ${({ bgColor }) => bgColor};
  background-image: ${({ bgImage }) => (bgImage ? `url(${bgImage})` : 'none')};
  background-size: cover;
  background-position: center;
  color: ${({ textColor }) => textColor};
`;

const HeroBannerContent = styled.div<{ hasInsetImage: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 520px;

  @media (min-width: 640px) {
    ${({ hasInsetImage }) =>
      hasInsetImage
        ? `
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      text-align: left;
      max-width: 720px;
    `
        : ''}
  }
`;

const HeroTextContent = styled.div`
  width: 100%;
`;

const HeroInsetImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
  background: rgba(255, 255, 255, 0.12);
`;

const HeroHeadline = styled.h1<{ textColor: string }>`
  margin: 0 0 10px;
  font-size: 28px;
  font-weight: 700;
  color: ${({ textColor }) => textColor};
`;

const HeroSubheadline = styled.p<{ textColor: string }>`
  margin: 0 0 20px;
  font-size: 16px;
  opacity: 0.9;
  color: ${({ textColor }) => textColor};
`;

const HeroCta = styled.span<{ textColor: string }>`
  display: inline-block;
  padding: 12px 28px;
  border: 2px solid ${({ textColor }) => textColor};
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  color: ${({ textColor }) => textColor};
`;

function HeroBanner({ block, offer }: { block: StepPageBlock; offer: FunnelOffer }) {
  const headline = str(block, 'headline', offer.headline);
  const subheadline = str(block, 'subheadline', offer.description);
  const ctaText = str(block, 'cta_text', offer.ctaText);
  const bgColor = str(block, 'bg_color', '#1a1c1d');
  const textColor = str(block, 'text_color', '#ffffff');
  // Only use background image if explicitly set in the block — don't fall back to offer image
  // (offer image as hero background covers the bg_color and looks wrong)
  const bgImage = str(block, 'image_url', '');
  const offerImage = useOfferImage(offer.imageUrl, offer.productId);
  const showInsetImage = !bgImage && Boolean(offerImage);

  return (
    <HeroBannerWrap bgColor={bgColor} textColor={textColor} bgImage={bgImage || undefined}>
      <HeroBannerContent hasInsetImage={showInsetImage}>
        <HeroTextContent>
          <HeroHeadline textColor={textColor}>{headline}</HeroHeadline>
          <HeroSubheadline textColor={textColor}>{subheadline}</HeroSubheadline>
          {ctaText && <HeroCta textColor={textColor}>{ctaText}</HeroCta>}
        </HeroTextContent>
        {showInsetImage && <HeroInsetImage src={offerImage ?? undefined} alt={offer.name || headline} />}
      </HeroBannerContent>
    </HeroBannerWrap>
  );
}

const ShowcaseWrap = styled.div`
  padding: 20px;
`;

const ProductGrid = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ columns }) => Math.min(columns, 3)}, 1fr);
  gap: 12px;
  margin-top: 12px;
`;

const ProductCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  padding: 12px;
  text-align: center;
  background: ${({ theme }) => theme.colors.background.secondary};
`;

const ProductImagePlaceholder = styled.div`
  width: 100%;
  height: 80px;
  background: ${({ theme }) => theme.colors.background.disabled};
  border-radius: ${({ theme }) => theme.radius.small};
  margin-bottom: 8px;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 80px;
  object-fit: contain;
  border-radius: ${({ theme }) => theme.radius.small};
  margin-bottom: 8px;
  background: ${({ theme }) => theme.colors.background.primary};
`;

const PriceStack = styled.div`
  display: flex;
  justify-content: center;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
`;

const OriginalPrice = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: line-through;
`;

function ProductShowcase({ block, offer, offers }: { block: StepPageBlock; offer: FunnelOffer; offers?: FunnelOffer[] }) {
  const title = str(block, 'title', 'You Might Also Like');
  const columns = num(block, 'columns', 3);
  const showPrice = bool(block, 'show_price', true);
  const productIdsRaw = block.props?.product_ids ?? block.props?.[toCamel('product_ids')];
  const productIdsRawKey = useMemo(
    () => (Array.isArray(productIdsRaw) ? productIdsRaw.join('\u0001') : ''),
    [productIdsRaw],
  );
  const productIds = useMemo(
    () =>
      Array.isArray(productIdsRaw)
        ? productIdsRaw.filter((id): id is string => typeof id === 'string' && id.trim().length > 0)
        : undefined,
    [productIdsRawKey],
  );
  const { products: relatedProducts, loading: loadingProducts } = useProductShowcase(productIds);
  const offerImage = useOfferImage(offer.imageUrl, offer.productId);
  const primaryProductName = offer.name || offer.headline || 'Offer Product';
  const showOriginalPrice =
    Number.isFinite(offer.originalPrice) &&
    Number.isFinite(offer.pricingValue) &&
    offer.originalPrice !== offer.pricingValue;
  const primaryItem: ProductShowcaseItem = {
    id: offer.productId || offer.id,
    name: primaryProductName,
    imageUrl: offerImage,
    price: offer.pricingValue,
    originalPrice: showOriginalPrice ? offer.originalPrice : null,
  };
  const fallbackItems: ProductShowcaseItem[] = [
    primaryItem,
    {
      id: 'related-item',
      name: 'Related Item',
      imageUrl: null,
      price: offer.pricingValue,
      originalPrice: null,
    },
    {
      id: 'bundle-deal',
      name: 'Bundle Deal',
      imageUrl: null,
      price: offer.pricingValue,
      originalPrice: null,
    },
  ];
  const multiOfferItems: ProductShowcaseItem[] | null =
    Array.isArray(offers) && offers.length > 0
      ? offers.slice(0, columns).map((item) => {
          const showOriginal =
            Number.isFinite(item.originalPrice) &&
            Number.isFinite(item.pricingValue) &&
            item.originalPrice !== item.pricingValue;
          return {
            id: item.productId || item.id,
            name: item.name || item.headline || 'Offer Product',
            imageUrl: item.imageUrl ?? null,
            price: item.pricingValue,
            originalPrice: showOriginal ? item.originalPrice : null,
          };
        })
      : null;
  const shouldUseFetchedProducts = Boolean(productIds?.length);
  const dedupedRelatedProducts = shouldUseFetchedProducts
    ? relatedProducts.filter((item) => item.id !== primaryItem.id)
    : relatedProducts;
  const items = shouldUseFetchedProducts
    ? [primaryItem, ...dedupedRelatedProducts].slice(0, columns)
    : multiOfferItems && multiOfferItems.length > 0
      ? multiOfferItems
    : fallbackItems.slice(0, columns);
  const loadingCards = shouldUseFetchedProducts && loadingProducts
    ? Math.max(columns - items.length, 0)
    : 0;

  return (
    <ShowcaseWrap>
      <SectionTitle>{title}</SectionTitle>
      <ProductGrid columns={columns}>
        {items.map((item, i) => (
          <ProductCard key={item.id || i}>
            {item.imageUrl ? (
              <ProductImage src={item.imageUrl} alt={item.name} />
            ) : (
              <ProductImagePlaceholder aria-hidden="true" />
            )}
            <BodyText style={{ fontWeight: 600, color: 'inherit', fontSize: '13px' }}>{item.name}</BodyText>
            {showPrice && (
              <PriceStack>
                {item.originalPrice !== null && <OriginalPrice>{formatPrice(item.originalPrice)}</OriginalPrice>}
                <BodyText style={{ fontSize: '13px' }}>{formatPrice(item.price)}</BodyText>
              </PriceStack>
            )}
          </ProductCard>
        ))}
        {Array.from({ length: loadingCards }, (_, i) => (
          <ProductCard key={`loading-${i}`}>
            <ProductImagePlaceholder aria-hidden="true" />
            <BodyText style={{ fontWeight: 600, color: 'inherit', fontSize: '13px' }}>Loading product...</BodyText>
            {showPrice && <BodyText style={{ fontSize: '13px' }}>{formatPrice(null)}</BodyText>}
          </ProductCard>
        ))}
      </ProductGrid>
    </ShowcaseWrap>
  );
}

const TestimonialWrap = styled.div<{ bgColor: string }>`
  padding: 24px 20px;
  background-color: ${({ bgColor }) => bgColor};
  text-align: center;
`;

const QuoteText = styled.blockquote`
  margin: 12px 0;
  font-size: 16px;
  font-style: italic;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.tertiary};
  &::before { content: '"'; }
  &::after { content: '"'; }
`;

const Stars = styled.div<{ color: string }>`
  font-size: 20px;
  color: ${({ color }) => color};
  letter-spacing: 2px;
`;

const ReviewAuthor = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

function TestimonialSection({ block }: { block: StepPageBlock }) {
  const title = str(block, 'title', 'What Our Customers Say');
  const bgColor = str(block, 'bg_color', '#f6f6f7');
  const showStars = bool(block, 'show_stars', true);

  return (
    <TestimonialWrap bgColor={bgColor}>
      <SectionTitle style={{ textAlign: 'center' }}>{title}</SectionTitle>
      {showStars && (
        <Stars color="#f59e0b" aria-label="5 out of 5 stars">
          ★★★★★
        </Stars>
      )}
      <QuoteText>This product completely changed my life. Absolutely love it!</QuoteText>
      <ReviewAuthor>— Sarah M., Verified Buyer</ReviewAuthor>
    </TestimonialWrap>
  );
}

const FeatureGridWrap = styled.div<{ bgColor: string }>`
  padding: 24px 20px;
  background-color: ${({ bgColor }) => bgColor};
`;

const FeatureList = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ columns }) => Math.min(columns, 4)}, 1fr);
  gap: 16px;
  margin-top: 12px;
`;

const FeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
`;

const FeatureIcon = styled.div<{ iconColor: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ iconColor }) => `${iconColor}22`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: ${({ iconColor }) => iconColor};
`;

const FEATURE_ICONS = ['✓', '★', '⚡', '♻', '✦', '◆'];
const FEATURE_LABELS = ['Quality', 'Top Rated', 'Fast', 'Sustainable', 'Secure', 'Premium'];

function FeatureGrid({ block }: { block: StepPageBlock }) {
  const title = str(block, 'title', 'Why Choose Us');
  const columns = num(block, 'columns', 3);
  const iconColor = str(block, 'icon_color', '#005bd3');
  const bgColor = str(block, 'bg_color', '#ffffff');

  return (
    <FeatureGridWrap bgColor={bgColor}>
      <SectionTitle>{title}</SectionTitle>
      <FeatureList columns={columns}>
        {Array.from({ length: columns }, (_, i) => (
          <FeatureItem key={i}>
            <FeatureIcon iconColor={iconColor} aria-hidden="true">
              {FEATURE_ICONS[i % FEATURE_ICONS.length]}
            </FeatureIcon>
            <BodyText style={{ fontWeight: 600, fontSize: '13px' }}>{FEATURE_LABELS[i % FEATURE_LABELS.length]}</BodyText>
          </FeatureItem>
        ))}
      </FeatureList>
    </FeatureGridWrap>
  );
}

const PricingWrap = styled.div`
  padding: 24px 20px;
`;

const PricingCards = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 12px;
`;

const PricingCard = styled.div<{ highlighted: boolean; accentColor: string }>`
  border: 2px solid ${({ highlighted, accentColor }) => (highlighted ? accentColor : '#e1e3e5')};
  border-radius: 12px;
  padding: 16px;
  position: relative;
  text-align: center;
`;

const PricingBadge = styled.span<{ accentColor: string }>`
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ accentColor }) => accentColor};
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 9999px;
  white-space: nowrap;
`;

const PricingName = styled.p`
  margin: 8px 0 4px;
  font-weight: 700;
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PricingPrice = styled.p<{ accentColor: string }>`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: ${({ accentColor }) => accentColor};
`;

function PricingTable({ block, offer, offers }: { block: StepPageBlock; offer: FunnelOffer; offers?: FunnelOffer[] }) {
  const title = str(block, 'title', 'Choose Your Plan');
  const accentColor = str(block, 'accent_color', '#005bd3');
  const showBadge = bool(block, 'show_badge', true);
  const multiOfferPlans = Array.isArray(offers) && offers.length > 0 ? offers : null;

  return (
    <PricingWrap>
      <SectionTitle>{title}</SectionTitle>
      <PricingCards>
        {multiOfferPlans ? (
          multiOfferPlans.map((plan, index) => {
            const highlighted = index === multiOfferPlans.length - 1;
            return (
              <PricingCard key={plan.id} highlighted={highlighted} accentColor={accentColor}>
                {highlighted && showBadge && <PricingBadge accentColor={accentColor}>Featured</PricingBadge>}
                <PricingName>{plan.name || plan.headline || `Plan ${index + 1}`}</PricingName>
                <PricingPrice accentColor={accentColor}>{formatPrice(plan.pricingValue)}</PricingPrice>
                {Number.isFinite(plan.originalPrice) && plan.originalPrice !== plan.pricingValue ? (
                  <BodyText style={{ fontSize: '12px', marginTop: '4px' }}>
                    Was {formatPrice(plan.originalPrice)}
                  </BodyText>
                ) : (
                  <BodyText style={{ fontSize: '12px', marginTop: '4px' }}>Current offer</BodyText>
                )}
              </PricingCard>
            );
          })
        ) : (
          <>
            <PricingCard highlighted={false} accentColor={accentColor}>
              <PricingName>Standard</PricingName>
              <PricingPrice accentColor={accentColor}>{formatPrice(offer.originalPrice)}</PricingPrice>
              <BodyText style={{ fontSize: '12px', marginTop: '4px' }}>Regular price</BodyText>
            </PricingCard>
            <PricingCard highlighted accentColor={accentColor}>
              {showBadge && <PricingBadge accentColor={accentColor}>Best Value</PricingBadge>}
              <PricingName>{str(block, 'highlight_plan', 'Pro')}</PricingName>
              <PricingPrice accentColor={accentColor}>{formatPrice(offer.pricingValue)}</PricingPrice>
              <BodyText style={{ fontSize: '12px', marginTop: '4px' }}>Special offer</BodyText>
            </PricingCard>
          </>
        )}
      </PricingCards>
    </PricingWrap>
  );
}

const FaqWrap = styled.div`
  padding: 20px;
`;

const FaqItem = styled.div<{ borderColor: string }>`
  border-bottom: 1px solid ${({ borderColor }) => borderColor};
  padding: 14px 0;
`;

const FaqQuestion = styled.p`
  margin: 0;
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FaqAnswer = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

const FAQ_ITEMS = [
  { q: 'What makes this offer special?', a: 'This is an exclusive discount available for a limited time only.' },
  { q: 'Is there a return policy?', a: 'Yes, we offer a 30-day satisfaction guarantee.' },
  { q: 'How quickly will I receive my order?', a: 'Most orders ship within 1-3 business days.' },
];

function FaqAccordion({ block }: { block: StepPageBlock }) {
  const title = str(block, 'title', 'Frequently Asked Questions');
  const borderColor = str(block, 'border_color', '#e1e3e5');

  return (
    <FaqWrap>
      <SectionTitle>{title}</SectionTitle>
      {FAQ_ITEMS.map((item, i) => (
        <FaqItem key={i} borderColor={borderColor}>
          <FaqQuestion>{item.q}</FaqQuestion>
          <FaqAnswer>{item.a}</FaqAnswer>
        </FaqItem>
      ))}
    </FaqWrap>
  );
}

const NewsletterWrap = styled.div<{ bgColor: string; textColor: string }>`
  padding: 32px 24px;
  background-color: ${({ bgColor }) => bgColor};
  text-align: center;
`;

const NewsletterHeadline = styled.h3<{ textColor: string }>`
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 700;
  color: ${({ textColor }) => textColor};
`;

const NewsletterDesc = styled.p<{ textColor: string }>`
  margin: 0 0 16px;
  font-size: 14px;
  opacity: 0.9;
  color: ${({ textColor }) => textColor};
`;

const NewsletterInputRow = styled.div`
  display: flex;
  gap: 8px;
  max-width: 360px;
  margin: 0 auto;
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: 10px 14px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
`;

const NewsletterBtn = styled.button<{ bgColor: string; textColor: string }>`
  padding: 10px 20px;
  background: ${({ textColor }) => textColor};
  color: ${({ bgColor }) => bgColor};
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
`;

function NewsletterSignup({ block }: { block: StepPageBlock }) {
  const headline = str(block, 'headline', 'Stay in the Loop');
  const description = str(block, 'description', 'Get exclusive deals and early access.');
  const ctaText = str(block, 'cta_text', 'Subscribe');
  const bgColor = str(block, 'bg_color', '#005bd3');
  const textColor = str(block, 'text_color', '#ffffff');

  return (
    <NewsletterWrap bgColor={bgColor} textColor={textColor}>
      <NewsletterHeadline textColor={textColor}>{headline}</NewsletterHeadline>
      <NewsletterDesc textColor={textColor}>{description}</NewsletterDesc>
      <NewsletterInputRow>
        <NewsletterInput type="email" placeholder="you@example.com" aria-label="Email address" readOnly />
        <NewsletterBtn type="button" bgColor={bgColor} textColor={textColor}>{ctaText}</NewsletterBtn>
      </NewsletterInputRow>
    </NewsletterWrap>
  );
}

const SocialBarWrap = styled.div<{ bgColor: string }>`
  padding: 14px 20px;
  background-color: ${({ bgColor }) => bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
`;

const SocialBarStars = styled.span<{ accentColor: string }>`
  font-size: 18px;
  color: ${({ accentColor }) => accentColor};
  letter-spacing: 2px;
`;

const SocialBarStat = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SocialBarLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

function SocialProofBar({ block }: { block: StepPageBlock }) {
  const rating = num(block, 'rating', 4.8);
  const reviewCount = num(block, 'review_count', 12400);
  const bgColor = str(block, 'bg_color', '#f6f6f7');
  const accentColor = str(block, 'accent_color', '#f59e0b');

  return (
    <SocialBarWrap bgColor={bgColor}>
      <SocialBarStars accentColor={accentColor} aria-label={`${rating} out of 5 stars`}>★★★★★</SocialBarStars>
      <div>
        <SocialBarStat>{rating.toFixed(1)} / 5</SocialBarStat>
        {' '}
        <SocialBarLabel>({reviewCount.toLocaleString()} reviews)</SocialBarLabel>
      </div>
    </SocialBarWrap>
  );
}

// ---------------------------------------------------------------------------
// Tier 2: Quick Sections
// ---------------------------------------------------------------------------

const ImageTextWrap = styled.div<{ imagePosition: string }>`
  display: flex;
  flex-direction: ${({ imagePosition }) => (imagePosition === 'right' ? 'row-reverse' : 'row')};
  gap: 20px;
  padding: 20px;
  align-items: center;
  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    flex-direction: column;
  }
`;

const ImageTextImg = styled.img`
  flex: 1;
  max-width: 45%;
  border-radius: ${({ theme }) => theme.radius.regular};
  object-fit: cover;
  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    max-width: 100%;
  }
`;

const ImageTextBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ImageTextHeadline = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

function ImageTextBlock({ block, offer }: { block: StepPageBlock; offer: FunnelOffer }) {
  const headline = str(block, 'headline', offer.headline);
  const bodyText = str(block, 'body_text', offer.description);
  const imageUrl = str(block, 'image_url', offer.imageUrl);
  const imagePosition = str(block, 'image_position', 'left');
  const ctaText = str(block, 'cta_text', '');

  return (
    <ImageTextWrap imagePosition={imagePosition}>
      {imageUrl && <ImageTextImg src={imageUrl} alt={headline} />}
      <ImageTextBody>
        <ImageTextHeadline>{headline}</ImageTextHeadline>
        <BodyText>{bodyText}</BodyText>
        {ctaText && (
          <span style={{ fontSize: '14px', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>
            {ctaText}
          </span>
        )}
      </ImageTextBody>
    </ImageTextWrap>
  );
}

const CtaGroupWrap = styled.div<{ alignment: string }>`
  display: flex;
  gap: 10px;
  justify-content: ${({ alignment }) => alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center'};
  flex-wrap: wrap;
  padding: 16px 20px;
`;

const CtaPrimaryBtn = styled.button<{ bgColor: string; textColor: string }>`
  padding: 12px 24px;
  background: ${({ bgColor }) => bgColor};
  color: ${({ textColor }) => textColor};
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  min-height: 44px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const CtaPrimaryLink = styled.a<{ bgColor: string; textColor: string }>`
  padding: 12px 24px;
  background: ${({ bgColor }) => bgColor};
  color: ${({ textColor }) => textColor};
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  min-height: 44px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const CtaSecondaryBtn = styled.button<{ bgColor: string; textColor: string }>`
  padding: 12px 24px;
  background: ${({ bgColor }) => bgColor};
  color: ${({ textColor }) => textColor};
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  min-height: 44px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const CtaSecondaryLink = styled.a<{ bgColor: string; textColor: string }>`
  padding: 12px 24px;
  background: ${({ bgColor }) => bgColor};
  color: ${({ textColor }) => textColor};
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  min-height: 44px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

function CtaButtonGroup({ block, offer, callbacks }: { block: StepPageBlock; offer: FunnelOffer; callbacks?: BlockCallbacks }) {
  const [primaryButton, secondaryButton] = ctaButtons(block);
  const primaryText =
    (typeof primaryButton?.text === 'string' && primaryButton.text.trim()) ||
    str(block, 'primary_text', '') ||
    str(block, 'acceptText', offer.ctaText);
  const secondaryText =
    (typeof secondaryButton?.text === 'string' && secondaryButton.text.trim()) ||
    str(block, 'secondary_text', '') ||
    str(block, 'declineText', offer.declineText);
  const primaryColor = str(block, 'primary_color', '#005bd3');
  const primaryTextColor = str(block, 'primary_text_color', '#ffffff');
  const secondaryColor = str(block, 'secondary_color', '#f1f2f3');
  const secondaryTextColor = str(block, 'secondary_text_color', '#202223');
  const alignment = str(block, 'alignment', 'center');
  const primaryUrl = typeof primaryButton?.url === 'string' ? primaryButton.url.trim() : '';
  const secondaryUrl = typeof secondaryButton?.url === 'string' ? secondaryButton.url.trim() : '';
  const isPrimaryAccept = !primaryUrl || primaryUrl === '#accept' || primaryUrl === '#add-to-cart';
  const isPrimaryLink = Boolean(primaryUrl) && !isPrimaryAccept && primaryUrl !== '#decline' && primaryUrl !== '#dismiss';
  const isSecondaryDecline = !secondaryUrl || secondaryUrl === '#decline' || secondaryUrl === '#dismiss';
  const isSecondaryLink = Boolean(secondaryUrl) && !isSecondaryDecline && secondaryUrl !== '#accept' && secondaryUrl !== '#add-to-cart';

  return (
    <CtaGroupWrap alignment={alignment}>
      {isPrimaryLink ? (
        <CtaPrimaryLink href={primaryUrl} bgColor={primaryColor} textColor={primaryTextColor}>
          {primaryText}
        </CtaPrimaryLink>
      ) : (
        <CtaPrimaryBtn
          type="button"
          bgColor={primaryColor}
          textColor={primaryTextColor}
          onClick={isPrimaryAccept ? callbacks?.onAccept : undefined}
          disabled={callbacks?.accepting}
        >
          {callbacks?.accepting ? 'Adding...' : primaryText}
        </CtaPrimaryBtn>
      )}
      {secondaryText ? (
        isSecondaryLink ? (
          <CtaSecondaryLink href={secondaryUrl} bgColor={secondaryColor} textColor={secondaryTextColor}>
            {secondaryText}
          </CtaSecondaryLink>
        ) : (
          <CtaSecondaryBtn
            type="button"
            bgColor={secondaryColor}
            textColor={secondaryTextColor}
            onClick={isSecondaryDecline ? callbacks?.onDecline : undefined}
            disabled={callbacks?.accepting}
          >
            {secondaryText}
          </CtaSecondaryBtn>
        )
      ) : null}
    </CtaGroupWrap>
  );
}

const TimerWrap = styled.div<{ bgColor: string }>`
  padding: 16px 20px;
  background-color: ${({ bgColor }) => bgColor};
  text-align: center;
`;

const TimerMessage = styled.p`
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TimerDigits = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
`;

const TimerBlock = styled.div<{ accentColor: string }>`
  background: ${({ accentColor }) => accentColor};
  color: #fff;
  border-radius: 6px;
  padding: 8px 14px;
  font-size: 22px;
  font-weight: 700;
  min-width: 48px;
`;

const TimerLabel = styled.span`
  font-size: 11px;
  display: block;
  opacity: 0.85;
`;

const TimerColon = styled.span<{ accentColor: string }>`
  font-size: 22px;
  font-weight: 700;
  color: ${({ accentColor }) => accentColor};
`;

function CountdownTimer({ block }: { block: StepPageBlock }) {
  const message = str(block, 'message', 'This offer expires in:');
  const minutes = num(block, 'minutes', 10);
  const showSeconds = bool(block, 'show_seconds', true);
  const accentColor = str(block, 'accent_color', '#d82c0d');
  const bgColor = str(block, 'bg_color', '#fff4f1');

  const [remainingMs, setRemainingMs] = useState<number>(() => minutes * 60 * 1000);

  useEffect(() => {
    const targetTime = Date.now() + minutes * 60 * 1000;

    const tick = () => {
      const remaining = targetTime - Date.now();
      if (remaining <= 0) {
        setRemainingMs(0);
        return;
      }
      setRemainingMs(remaining);
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [minutes]);

  const totalSeconds = Math.floor(remainingMs / 1000);
  const displayHours = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
  const displayMinutes = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
  const displaySeconds = (totalSeconds % 60).toString().padStart(2, '0');

  return (
    <TimerWrap bgColor={bgColor}>
      <TimerMessage>{message}</TimerMessage>
      <TimerDigits aria-label={`${displayHours} hours ${displayMinutes} minutes ${displaySeconds} seconds remaining`}>
        <TimerBlock accentColor={accentColor}>
          {displayHours}
          <TimerLabel>HRS</TimerLabel>
        </TimerBlock>
        <TimerColon accentColor={accentColor}>:</TimerColon>
        <TimerBlock accentColor={accentColor}>
          {displayMinutes}
          <TimerLabel>MIN</TimerLabel>
        </TimerBlock>
        {showSeconds && (
          <>
            <TimerColon accentColor={accentColor}>:</TimerColon>
            <TimerBlock accentColor={accentColor}>
              {displaySeconds}
              <TimerLabel>SEC</TimerLabel>
            </TimerBlock>
          </>
        )}
      </TimerDigits>
    </TimerWrap>
  );
}

const ProgressWrap = styled.div`
  padding: 16px 20px;
`;

const ProgressLabel = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ProgressTrack = styled.div<{ bgColor: string }>`
  height: 8px;
  border-radius: 9999px;
  background: ${({ bgColor }) => bgColor};
  overflow: hidden;
`;

const ProgressFill = styled.div<{ percent: number; fillColor: string }>`
  height: 100%;
  width: ${({ percent }) => Math.min(100, Math.max(0, percent))}%;
  background: ${({ fillColor }) => fillColor};
  border-radius: 9999px;
  transition: width 0.3s ease;
`;

function ProgressBar({ block }: { block: StepPageBlock }) {
  const totalSteps = num(block, 'total_steps', 3);
  const currentStep = num(block, 'current_step', 1);
  const label = str(block, 'label', 'Step {current} of {total}')
    .replace('{current}', String(currentStep))
    .replace('{total}', String(totalSteps));
  const fillColor = str(block, 'fill_color', '#005bd3');
  const bgColor = str(block, 'bg_color', '#e1e3e5');
  const percent = totalSteps > 0 ? (currentStep / totalSteps) * 100 : 0;

  return (
    <ProgressWrap>
      <ProgressLabel>{label}</ProgressLabel>
      <ProgressTrack bgColor={bgColor} role="progressbar" aria-valuenow={currentStep} aria-valuemin={1} aria-valuemax={totalSteps}>
        <ProgressFill percent={percent} fillColor={fillColor} />
      </ProgressTrack>
    </ProgressWrap>
  );
}

const VideoWrap = styled.div<{ aspectRatio: string }>`
  width: 100%;
  aspect-ratio: ${({ aspectRatio }) => aspectRatio.replace(':', '/')};
  background: #000;
  border-radius: ${({ theme }) => theme.radius.regular};
  overflow: hidden;
  position: relative;
`;

const VideoIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const VideoPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background.secondary};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

function buildEmbedUrl(rawUrl: string): string | null {
  if (!rawUrl) return null;
  const youtubeMatch = rawUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (youtubeMatch) return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  const vimeoMatch = rawUrl.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return rawUrl;
}

function VideoEmbed({ block }: { block: StepPageBlock }) {
  const rawUrl = str(block, 'video_url', '');
  const aspectRatio = str(block, 'aspect_ratio', '16:9');
  const embedUrl = buildEmbedUrl(rawUrl);

  return (
    <VideoWrap aspectRatio={aspectRatio}>
      {embedUrl ? (
        <VideoIframe src={embedUrl} title="Embedded video" allowFullScreen />
      ) : (
        <VideoPlaceholder>Video placeholder</VideoPlaceholder>
      )}
    </VideoWrap>
  );
}

const IconListWrap = styled.ul`
  list-style: none;
  margin: 0;
  padding: 12px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const IconListItem = styled.li<{ iconColor: string; textColor: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: ${({ textColor }) => textColor};
`;

const IconListDot = styled.span<{ iconColor: string }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ iconColor }) => iconColor};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
`;

function IconList({ block }: { block: StepPageBlock }) {
  const items = str(block, 'items', 'Free shipping\nSatisfaction guarantee\n30-day returns');
  const iconColor = str(block, 'icon_color', '#008060');
  const textColor = str(block, 'text_color', '#202223');
  const itemLines = items.split('\n').filter(Boolean);

  return (
    <IconListWrap>
      {itemLines.map((item, i) => (
        <IconListItem key={i} iconColor={iconColor} textColor={textColor}>
          <IconListDot iconColor={iconColor} aria-hidden="true">✓</IconListDot>
          {item}
        </IconListItem>
      ))}
    </IconListWrap>
  );
}

const StatsWrap = styled.div<{ bgColor: string }>`
  padding: 24px 20px;
  background-color: ${({ bgColor }) => bgColor};
`;

const StatsGrid = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ columns }) => Math.min(columns, 4)}, 1fr);
  gap: 16px;
  text-align: center;
`;

const StatNumber = styled.p<{ accentColor: string }>`
  margin: 0 0 4px;
  font-size: 28px;
  font-weight: 700;
  color: ${({ accentColor }) => accentColor};
`;

const StatLabel = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const STAT_DATA = [
  { number: '50K+', label: 'Happy Customers' },
  { number: '4.9', label: 'Avg. Rating' },
  { number: '99%', label: 'Satisfaction' },
  { number: '30', label: 'Day Returns' },
];

function StatsCounter({ block }: { block: StepPageBlock }) {
  const columns = num(block, 'columns', 3);
  const accentColor = str(block, 'accent_color', '#005bd3');
  const bgColor = str(block, 'bg_color', '#ffffff');

  return (
    <StatsWrap bgColor={bgColor}>
      <StatsGrid columns={columns}>
        {STAT_DATA.slice(0, columns).map((stat, i) => (
          <div key={i}>
            <StatNumber accentColor={accentColor}>{stat.number}</StatNumber>
            <StatLabel>{stat.label}</StatLabel>
          </div>
        ))}
      </StatsGrid>
    </StatsWrap>
  );
}

const ComparisonWrap = styled.div`
  padding: 20px;
  overflow-x: auto;
`;

const ComparisonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const ComparisonHead = styled.thead``;

const ComparisonTh = styled.th<{ highlighted: boolean; accentColor: string }>`
  padding: 10px 12px;
  text-align: center;
  font-weight: 700;
  background: ${({ highlighted, accentColor }) => (highlighted ? accentColor : '#f5f5f5')};
  color: ${({ highlighted }) => (highlighted ? '#fff' : 'inherit')};
  border: 1px solid #e1e3e5;
`;

const ComparisonTd = styled.td<{ striped: boolean }>`
  padding: 8px 12px;
  text-align: center;
  border: 1px solid #e1e3e5;
  background: ${({ striped }) => (striped ? '#fafafa' : '#fff')};
`;

const COMPARISON_ROWS = [
  ['Quality', '✓', '✓'],
  ['Free Shipping', '✓', '✗'],
  ['Money-back Guarantee', '✓', '✗'],
  ['24/7 Support', '✓', '✗'],
];

function ComparisonTableBlock({ block }: { block: StepPageBlock }) {
  const title = str(block, 'title', 'How Do We Compare?');
  const highlightColumn = num(block, 'highlight_column', 1);
  const accentColor = str(block, 'accent_color', '#005bd3');
  const stripeRows = bool(block, 'stripe_rows', true);

  return (
    <ComparisonWrap>
      <SectionTitle>{title}</SectionTitle>
      <ComparisonTable>
        <ComparisonHead>
          <tr>
            <ComparisonTh highlighted={false} accentColor={accentColor}>Feature</ComparisonTh>
            <ComparisonTh highlighted={highlightColumn === 1} accentColor={accentColor}>Us</ComparisonTh>
            <ComparisonTh highlighted={highlightColumn === 2} accentColor={accentColor}>Others</ComparisonTh>
          </tr>
        </ComparisonHead>
        <tbody>
          {COMPARISON_ROWS.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <ComparisonTd key={j} striped={stripeRows && i % 2 === 1}>{cell}</ComparisonTd>
              ))}
            </tr>
          ))}
        </tbody>
      </ComparisonTable>
    </ComparisonWrap>
  );
}

const BadgeWrap = styled.div<{ position: string }>`
  position: relative;
  display: inline-flex;
  align-items: ${({ position }) => position.startsWith('bottom') ? 'flex-end' : 'flex-start'};
  justify-content: ${({ position }) => position.endsWith('right') ? 'flex-end' : 'flex-start'};
  padding: 8px;
`;

const BadgeTag = styled.span<{ bgColor: string; textColor: string }>`
  background: ${({ bgColor }) => bgColor};
  color: ${({ textColor }) => textColor};
  font-size: 12px;
  font-weight: 700;
  padding: 4px 14px;
  border-radius: 4px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

function BadgeRibbon({ block }: { block: StepPageBlock }) {
  const text = str(block, 'text', 'SALE');
  const position = str(block, 'position', 'top-right');
  const bgColor = str(block, 'bg_color', '#d82c0d');
  const textColor = str(block, 'text_color', '#ffffff');

  return (
    <BadgeWrap position={position}>
      <BadgeTag bgColor={bgColor} textColor={textColor}>{text}</BadgeTag>
    </BadgeWrap>
  );
}

// ---------------------------------------------------------------------------
// Tier 3: Building Blocks
// ---------------------------------------------------------------------------

const HEADING_SIZES: Record<string, string> = {
  h1: '2rem',
  h2: '1.5rem',
  h3: '1.25rem',
  h4: '1.1rem',
  h5: '1rem',
  h6: '0.875rem',
};

const HEADING_WEIGHTS: Record<string, number> = {
  h1: 800, h2: 700, h3: 700, h4: 600, h5: 600, h6: 600,
};

function HeadingBlock({ block, offer }: { block: StepPageBlock; offer: FunnelOffer }) {
  const text = str(block, 'text', offer.headline);
  const level = str(block, 'level', 'h2') as keyof JSX.IntrinsicElements;
  const alignment = str(block, 'alignment', 'left') as React.CSSProperties['textAlign'];
  const color = str(block, 'color', '#202223');
  const Tag = level as React.ElementType;

  return (
    <Tag
      style={{
        margin: '0 0 8px',
        fontSize: HEADING_SIZES[level] ?? '1.5rem',
        fontWeight: HEADING_WEIGHTS[level] ?? 700,
        textAlign: alignment,
        color,
        lineHeight: 1.25,
      }}
    >
      {text}
    </Tag>
  );
}

function ParagraphBlock({ block, offer }: { block: StepPageBlock; offer: FunnelOffer }) {
  const text = str(block, 'text', offer.description);
  const fontSize = num(block, 'font_size', 16);
  const color = str(block, 'color', '#6d7175');
  const alignment = str(block, 'alignment', 'left') as React.CSSProperties['textAlign'];
  const lineHeight = num(block, 'line_height', 1.6);

  return (
    <p style={{ margin: 0, fontSize: `${fontSize}px`, color, textAlign: alignment, lineHeight }}>
      {text}
    </p>
  );
}

function ImageBlock({ block, offer }: { block: StepPageBlock; offer: FunnelOffer }) {
  const src = str(block, 'src', offer.imageUrl);
  const alt = str(block, 'alt', offer.headline);
  const borderRadius = num(block, 'border_radius', 8);
  const width = num(block, 'width', 100);

  if (!src) return null;

  return (
    <img
      src={src}
      alt={alt}
      style={{
        display: 'block',
        width: `${width}%`,
        borderRadius: `${borderRadius}px`,
        objectFit: 'contain',
        maxHeight: '400px',
      }}
    />
  );
}

const StyledButton = styled.button<{ bgColor: string; textColor: string; borderRadius: number; fullWidth: boolean }>`
  display: ${({ fullWidth }) => (fullWidth ? 'flex' : 'inline-flex')};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  background: ${({ bgColor }) => bgColor};
  color: ${({ textColor }) => textColor};
  border: none;
  border-radius: ${({ borderRadius }) => borderRadius}px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  min-height: 44px;
  text-decoration: none;
`;

function ButtonBlock({ block, offer }: { block: StepPageBlock; offer: FunnelOffer }) {
  const label = str(block, 'label', offer.ctaText);
  const bgColor = str(block, 'bg_color', '#005bd3');
  const textColor = str(block, 'text_color', '#ffffff');
  const borderRadius = num(block, 'border_radius', 6);
  const fullWidth = bool(block, 'full_width', false);

  return (
    <StyledButton type="button" bgColor={bgColor} textColor={textColor} borderRadius={borderRadius} fullWidth={fullWidth}>
      {label}
    </StyledButton>
  );
}

const ContainerBlock = styled.div<{ layout: string; direction: string; gap: number; padding: number; bgColor: string; borderRadius: number }>`
  display: ${({ layout }) => layout};
  flex-direction: ${({ direction }) => direction};
  gap: ${({ gap }) => gap}px;
  padding: ${({ padding }) => padding}px;
  background-color: ${({ bgColor }) => bgColor};
  border-radius: ${({ borderRadius }) => borderRadius}px;
  box-sizing: border-box;
`;

function ContainerWrapper({ block }: { block: StepPageBlock }) {
  const layout = str(block, 'layout', 'flex');
  const direction = str(block, 'direction', 'row');
  const gap = num(block, 'gap', 16);
  const padding = num(block, 'padding', 16);
  const bgColor = str(block, 'bg_color', '#ffffff');
  const borderRadius = num(block, 'border_radius', 8);

  return (
    <ContainerBlock layout={layout} direction={direction} gap={gap} padding={padding} bgColor={bgColor} borderRadius={borderRadius} />
  );
}

const CardBlock = styled.div<{ padding: number; borderColor: string; borderRadius: number; bgColor: string; showShadow: boolean }>`
  padding: ${({ padding }) => padding}px;
  border: 1px solid ${({ borderColor }) => borderColor};
  border-radius: ${({ borderRadius }) => borderRadius}px;
  background-color: ${({ bgColor }) => bgColor};
  box-shadow: ${({ showShadow }) => (showShadow ? '0 4px 6px -1px rgba(0,0,0,0.1)' : 'none')};
`;

function CardWrapper({ block }: { block: StepPageBlock }) {
  const padding = num(block, 'padding', 24);
  const borderColor = str(block, 'border_color', '#e1e3e5');
  const borderRadius = num(block, 'border_radius', 12);
  const bgColor = str(block, 'bg_color', '#ffffff');
  const showShadow = bool(block, 'show_shadow', true);

  return <CardBlock padding={padding} borderColor={borderColor} borderRadius={borderRadius} bgColor={bgColor} showShadow={showShadow} />;
}

const InputWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InputLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StyledInput = styled.input<{ borderColor: string }>`
  padding: 10px 14px;
  border: 1px solid ${({ borderColor }) => borderColor};
  border-radius: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background.primary};
  width: 100%;
  box-sizing: border-box;
  outline: none;
  pointer-events: none;
`;

function InputFieldBlock({ block }: { block: StepPageBlock }) {
  const label = str(block, 'label', 'Email Address');
  const placeholder = str(block, 'placeholder', 'you@example.com');
  const inputType = str(block, 'input_type', 'email');
  const borderColor = str(block, 'border_color', '#e1e3e5');

  return (
    <InputWrap>
      <InputLabel>{label}</InputLabel>
      <StyledInput
        type={inputType}
        placeholder={placeholder}
        borderColor={borderColor}
        readOnly
        aria-label={label}
        tabIndex={-1}
      />
    </InputWrap>
  );
}

const CheckboxWrap = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const CheckboxInput = styled.input<{ accentColor: string }>`
  width: 18px;
  height: 18px;
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: ${({ accentColor }) => accentColor};
  pointer-events: none;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;
`;

function CheckboxBlock({ block }: { block: StepPageBlock }) {
  const label = str(block, 'label', 'I agree to the terms and conditions');
  const defaultChecked = bool(block, 'default_checked', false);
  const accentColor = str(block, 'accent_color', '#005bd3');
  const reactId = useId();
  const checkboxId = block.id ? `checkbox-${block.id}` : `checkbox-${reactId}`;

  return (
    <CheckboxWrap>
      <CheckboxInput
        type="checkbox"
        defaultChecked={defaultChecked}
        accentColor={accentColor}
        readOnly
        tabIndex={-1}
        id={checkboxId}
      />
      <CheckboxLabel htmlFor={checkboxId}>{label}</CheckboxLabel>
    </CheckboxWrap>
  );
}

const SelectWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SelectLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StyledSelect = styled.select<{ borderColor: string }>`
  padding: 10px 14px;
  border: 1px solid ${({ borderColor }) => borderColor};
  border-radius: 6px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background.primary};
  width: 100%;
  box-sizing: border-box;
  pointer-events: none;
`;

function SelectBlock({ block }: { block: StepPageBlock }) {
  const label = str(block, 'label', 'Select an option');
  const placeholder = str(block, 'placeholder', 'Choose...');
  const options = str(block, 'options', 'Option 1\nOption 2\nOption 3');
  const borderColor = str(block, 'border_color', '#e1e3e5');
  const optionLines = options.split('\n').filter(Boolean);

  return (
    <SelectWrap>
      <SelectLabel>{label}</SelectLabel>
      <StyledSelect borderColor={borderColor} defaultValue="" aria-label={label} tabIndex={-1}>
        <option value="" disabled>{placeholder}</option>
        {optionLines.map((opt, i) => (
          <option key={i} value={opt}>{opt}</option>
        ))}
      </StyledSelect>
    </SelectWrap>
  );
}

function StyledDivider({ block }: { block: StepPageBlock }) {
  const color = str(block, 'color', '#e1e3e5');
  const thickness = num(block, 'thickness', 1);
  const marginTop = num(block, 'margin_top', 16);
  const marginBottom = num(block, 'margin_bottom', 16);
  const style = str(block, 'style', 'solid') as React.CSSProperties['borderStyle'];

  return (
    <hr
      style={{
        border: 'none',
        borderTop: `${thickness}px ${style} ${color}`,
        marginTop: `${marginTop}px`,
        marginBottom: `${marginBottom}px`,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Interactive
// ---------------------------------------------------------------------------

const SpinWheelOverlay = styled.div<{ bgColor: string }>`
  position: relative;
  width: 100%;
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ bgColor }) => bgColor};
  padding: 32px 24px;
  box-sizing: border-box;
`;

const SpinWheelModal = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 380px;
  width: 100%;
`;

const SpinWheelTitle = styled.h2<{ textColor: string }>`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: ${({ textColor }) => textColor};
  text-align: center;
`;

const SpinWheelSubtitle = styled.p<{ textColor: string }>`
  margin: 0;
  font-size: 14px;
  text-align: center;
  opacity: 0.85;
  color: ${({ textColor }) => textColor};
`;

const SpinWheelCanvas = styled.div<{ gradient: string; accentColor: string }>`
  position: relative;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: conic-gradient(${({ gradient }) => gradient});
  box-shadow: 0 0 0 6px ${({ accentColor }) => accentColor},
              0 8px 32px rgba(0, 0, 0, 0.4);
  flex-shrink: 0;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }
`;

const SpinWheelPointer = styled.div<{ accentColor: string }>`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 20px solid ${({ accentColor }) => accentColor};
  z-index: 1;
`;

const SpinSegmentList = styled.ul<{ textColor: string }>`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
  max-width: 320px;
`;

const SpinSegmentPill = styled.li<{ bgColor: string; textColor: string }>`
  background-color: ${({ bgColor }) => bgColor};
  color: ${({ textColor }) => textColor};
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  white-space: nowrap;
`;

const SpinEmailInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  border-radius: 6px;
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 14px;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const SpinButton = styled.button<{ accentColor: string }>`
  width: 100%;
  padding: 12px 20px;
  border: none;
  border-radius: 6px;
  background-color: ${({ accentColor }) => accentColor};
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: default;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
`;

function buildConicGradient(segments: string[], accentColor: string): string {
  const count = segments.length;
  if (count === 0) return accentColor;

  // Derive an alternate color by darkening the accent slightly
  const alternate = '#d97706'; // darker amber — safe fallback for any accent

  const sliceDeg = 360 / count;
  return segments
    .map((_, i) => {
      const color = i % 2 === 0 ? accentColor : alternate;
      const start = i * sliceDeg;
      const end = (i + 1) * sliceDeg;
      return `${color} ${start.toFixed(2)}deg ${end.toFixed(2)}deg`;
    })
    .join(', ');
}

function SpinTheWheel({ block }: { block: StepPageBlock }) {
  const title = str(block, 'title', 'Spin to Win!');
  const subtitle = str(block, 'subtitle', 'Enter your email for a chance to win a prize');
  const segmentsRaw = str(
    block,
    'segments',
    'Free Shipping\n10% OFF\n15% OFF\n20% OFF\nTry Again\n25% OFF',
  );
  const bgColor = str(block, 'bg_color', '#1a1c1d');
  const accentColor = str(block, 'accent_color', '#f59e0b');
  const textColor = str(block, 'text_color', '#ffffff');
  const requireEmail = bool(block, 'require_email', true);
  const buttonText = str(block, 'button_text', 'SPIN NOW');

  const segments = segmentsRaw
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

  const gradient = buildConicGradient(segments, accentColor);

  return (
    <SpinWheelOverlay bgColor={bgColor}>
      <SpinWheelModal>
        <SpinWheelTitle textColor={textColor}>{title}</SpinWheelTitle>
        <SpinWheelSubtitle textColor={textColor}>{subtitle}</SpinWheelSubtitle>

        <div style={{ position: 'relative' }}>
          <SpinWheelPointer accentColor={accentColor} />
          <SpinWheelCanvas gradient={gradient} accentColor={accentColor} />
        </div>

        <SpinSegmentList textColor={textColor}>
          {segments.map((seg, i) => (
            <SpinSegmentPill
              key={i}
              bgColor={i % 2 === 0 ? accentColor : 'rgba(255,255,255,0.12)'}
              textColor={i % 2 === 0 ? '#1a1c1d' : textColor}
            >
              {seg}
            </SpinSegmentPill>
          ))}
        </SpinSegmentList>

        {requireEmail && (
          <SpinEmailInput
            type="email"
            placeholder="Enter your email..."
            readOnly
            aria-label="Email address for spin wheel entry"
          />
        )}

        <SpinButton accentColor={accentColor}>{buttonText}</SpinButton>
      </SpinWheelModal>
    </SpinWheelOverlay>
  );
}

// ---------------------------------------------------------------------------
// Popup-specific components
// ---------------------------------------------------------------------------

// ── popup-close-button ──────────────────────────────────────────────────────

const CloseButtonOuter = styled.div<{ position: string; offsetX: number; offsetY: number }>`
  position: absolute;
  ${({ position, offsetX, offsetY }) =>
    position === 'top-left'
      ? `top: ${offsetY}px; left: ${offsetX}px;`
      : `top: ${offsetY}px; right: ${offsetX}px;`}
`;

const CloseButtonInner = styled.button<{ size: number }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
  border-radius: 50%;
  line-height: 1;
`;

const CloseIcon = styled.span<{ color: string; hoverColor: string; size: number }>`
  font-size: ${({ size }) => Math.round(size * 0.55)}px;
  color: ${({ color }) => color};
  font-style: normal;
  line-height: 1;
  user-select: none;
  transition: color 0.15s ease;

  button:hover > & {
    color: ${({ hoverColor }) => hoverColor};
  }
`;

function PopupCloseButton({ block }: { block: StepPageBlock }) {
  const position = str(block, 'position', 'top-right');
  const size = num(block, 'size', 32);
  const color = str(block, 'color', '#6b7280');
  const hoverColor = str(block, 'hoverColor', '#111827');
  const offsetX = num(block, 'offsetX', 12);
  const offsetY = num(block, 'offsetY', 12);

  const handleClose = () => {
    window.dispatchEvent(new CustomEvent('funnel:dismiss'));
  };

  return (
    <CloseButtonOuter position={position} offsetX={offsetX} offsetY={offsetY}>
      <CloseButtonInner type="button" size={size} aria-label="Close popup" onClick={handleClose}>
        <CloseIcon color={color} hoverColor={hoverColor} size={size} aria-hidden="true">
          &#x2715;
        </CloseIcon>
      </CloseButtonInner>
    </CloseButtonOuter>
  );
}

// ── popup-headline ──────────────────────────────────────────────────────────

const PopupHeadlineWrap = styled.div<{ alignment: string }>`
  text-align: ${({ alignment }) => alignment};
  width: 100%;
`;

const PopupHeadlineImg = styled.img`
  max-width: 100%;
  max-height: 80px;
  object-fit: contain;
`;

const PopupHeadlineText = styled.h2<{ fontSize: number; fontWeight: number; color: string }>`
  margin: 0;
  font-size: ${({ fontSize }) => fontSize}px;
  font-weight: ${({ fontWeight }) => fontWeight};
  color: ${({ color }) => color};
  line-height: 1.2;
`;

function PopupHeadline({ block }: { block: StepPageBlock }) {
  const text = str(block, 'text', "Don't miss out!");
  const imageUrl = str(block, 'imageUrl', '');
  const emoji = str(block, 'emoji', '');
  const fontSize = num(block, 'fontSize', 28);
  const fontWeight = num(block, 'fontWeight', 700);
  const color = str(block, 'color', '#111827');
  const alignment = str(block, 'alignment', 'center');

  return (
    <PopupHeadlineWrap alignment={alignment}>
      {imageUrl ? (
        <PopupHeadlineImg src={imageUrl} alt={text} />
      ) : (
        <PopupHeadlineText fontSize={fontSize} fontWeight={fontWeight} color={color}>
          {emoji ? `${emoji} ${text}` : text}
        </PopupHeadlineText>
      )}
    </PopupHeadlineWrap>
  );
}

// ── discount-hero ───────────────────────────────────────────────────────────

const DiscountHeroWrap = styled.div<{ bgColor: string; layout: string }>`
  display: flex;
  flex-direction: ${({ layout }) => (layout === 'horizontal' ? 'row' : 'column')};
  align-items: center;
  justify-content: center;
  gap: ${({ layout }) => (layout === 'horizontal' ? '8px' : '4px')};
  padding: 16px;
  background-color: ${({ bgColor }) => bgColor || 'transparent'};
  text-align: center;
  width: 100%;
  box-sizing: border-box;
`;

const DiscountLabel = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DiscountValue = styled.span<{ valueColor: string; valueFontSize: number }>`
  font-size: ${({ valueFontSize }) => valueFontSize}px;
  font-weight: 800;
  color: ${({ valueColor }) => valueColor};
  line-height: 1;
`;

function DiscountHero({ block }: { block: StepPageBlock }) {
  const prefix = str(block, 'prefix', 'Save');
  const value = str(block, 'value', '15%');
  const suffix = str(block, 'suffix', 'OFF');
  const layout = str(block, 'layout', 'stacked') as 'stacked' | 'horizontal';
  const valueColor = str(block, 'valueColor', '#22c55e');
  const valueFontSize = num(block, 'valueFontSize', 64);
  const bgColor = str(block, 'bg_color', '');

  return (
    <DiscountHeroWrap bgColor={bgColor} layout={layout}>
      {prefix && <DiscountLabel>{prefix}</DiscountLabel>}
      <DiscountValue valueColor={valueColor} valueFontSize={valueFontSize}>{value}</DiscountValue>
      {suffix && <DiscountLabel>{suffix}</DiscountLabel>}
    </DiscountHeroWrap>
  );
}

// ── shared text input styled component ──────────────────────────────────────

/** Shared base for email-input, phone-input, and name-input blocks. */
const StyledTextInput = styled.input<{
  borderColor: string;
  focusBorderColor: string;
  borderRadius: number;
  fontSize: number;
}>`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  border: 1.5px solid ${({ borderColor }) => borderColor};
  border-radius: ${({ borderRadius }) => borderRadius}px;
  font-size: ${({ fontSize }) => fontSize}px;
  outline: none;
  background: transparent;
  color: inherit;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: ${({ focusBorderColor }) => focusBorderColor};
    box-shadow: 0 0 0 3px ${({ focusBorderColor }) => focusBorderColor}33;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

// ── email-input ─────────────────────────────────────────────────────────────

const StyledEmailInput = StyledTextInput;

function EmailInputBlock({ block }: { block: StepPageBlock }) {
  const placeholder = str(block, 'placeholder', 'Enter your email');
  const borderColor = str(block, 'borderColor', '#d1d5db');
  const focusBorderColor = str(block, 'focusBorderColor', '#3b82f6');
  const borderRadius = num(block, 'borderRadius', 8);
  const fontSize = num(block, 'fontSize', 16);

  return (
    <StyledEmailInput
      type="email"
      placeholder={placeholder}
      borderColor={borderColor}
      focusBorderColor={focusBorderColor}
      borderRadius={borderRadius}
      fontSize={fontSize}
      aria-label={placeholder}
      autoComplete="email"
      readOnly
    />
  );
}

// ── phone-input ─────────────────────────────────────────────────────────────

const StyledPhoneInput = StyledTextInput;

function PhoneInputBlock({ block }: { block: StepPageBlock }) {
  const placeholder = str(block, 'placeholder', 'Phone number');
  const borderColor = str(block, 'borderColor', '#d1d5db');
  const focusBorderColor = str(block, 'focusBorderColor', '#3b82f6');
  const borderRadius = num(block, 'borderRadius', 8);
  const fontSize = num(block, 'fontSize', 16);

  return (
    <StyledPhoneInput
      type="tel"
      placeholder={placeholder}
      borderColor={borderColor}
      focusBorderColor={focusBorderColor}
      borderRadius={borderRadius}
      fontSize={fontSize}
      aria-label={placeholder}
      autoComplete="tel"
      readOnly
    />
  );
}

// ── name-input ───────────────────────────────────────────────────────────────

const StyledNameInput = StyledTextInput;

function NameInputBlock({ block }: { block: StepPageBlock }) {
  const placeholder = str(block, 'placeholder', 'Full name');
  const borderColor = str(block, 'borderColor', '#d1d5db');
  const focusBorderColor = str(block, 'focusBorderColor', '#3b82f6');
  const borderRadius = num(block, 'borderRadius', 8);
  const fontSize = num(block, 'fontSize', 16);

  return (
    <StyledNameInput
      type="text"
      placeholder={placeholder}
      borderColor={borderColor}
      focusBorderColor={focusBorderColor}
      borderRadius={borderRadius}
      fontSize={fontSize}
      aria-label={placeholder}
      autoComplete="name"
      readOnly
    />
  );
}

// ── address-input ────────────────────────────────────────────────────────────

const StyledAddressTextarea = styled.textarea<{
  borderColor: string;
  focusBorderColor: string;
  borderRadius: number;
  fontSize: number;
}>`
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  border: 1.5px solid ${({ borderColor }) => borderColor};
  border-radius: ${({ borderRadius }) => borderRadius}px;
  font-size: ${({ fontSize }) => fontSize}px;
  outline: none;
  background: transparent;
  color: inherit;
  transition: border-color 0.15s ease;
  resize: vertical;
  font-family: inherit;
  line-height: 1.5;

  &:focus {
    border-color: ${({ focusBorderColor }) => focusBorderColor};
    box-shadow: 0 0 0 3px ${({ focusBorderColor }) => focusBorderColor}33;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

function AddressInputBlock({ block }: { block: StepPageBlock }) {
  const placeholder = str(block, 'placeholder', 'Enter your address');
  const borderColor = str(block, 'borderColor', '#d1d5db');
  const focusBorderColor = str(block, 'focusBorderColor', '#3b82f6');
  const borderRadius = num(block, 'borderRadius', 8);
  const fontSize = num(block, 'fontSize', 16);
  const rows = num(block, 'rows', 3);

  return (
    <StyledAddressTextarea
      placeholder={placeholder}
      borderColor={borderColor}
      focusBorderColor={focusBorderColor}
      borderRadius={borderRadius}
      fontSize={fontSize}
      rows={rows}
      aria-label={placeholder}
      autoComplete="street-address"
      readOnly
    />
  );
}

// ── consent-checkbox ─────────────────────────────────────────────────────────

const ConsentCheckboxWrap = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const ConsentCheckboxInput = styled.input<{ checkboxSize: number }>`
  width: ${({ checkboxSize }) => checkboxSize}px;
  height: ${({ checkboxSize }) => checkboxSize}px;
  min-width: ${({ checkboxSize }) => checkboxSize}px;
  margin: 0;
  cursor: pointer;
  accent-color: #3b82f6;
  flex-shrink: 0;
`;

const ConsentCheckboxLabel = styled.label<{ fontSize: number; color: string }>`
  font-size: ${({ fontSize }) => fontSize}px;
  color: ${({ color }) => color};
  line-height: 1.4;
  cursor: pointer;
  user-select: none;
`;

function ConsentCheckboxBlock({ block }: { block: StepPageBlock }) {
  const label = str(block, 'label', 'I agree to receive marketing emails');
  const fontSize = num(block, 'fontSize', 13);
  const color = str(block, 'color', '#6b7280');
  const checkboxSize = num(block, 'checkboxSize', 18);
  const inputId = `consent-checkbox-${block.id}`;

  return (
    <ConsentCheckboxWrap>
      <ConsentCheckboxInput
        id={inputId}
        type="checkbox"
        checkboxSize={checkboxSize}
        aria-label={label}
        readOnly
      />
      <ConsentCheckboxLabel htmlFor={inputId} fontSize={fontSize} color={color}>
        {label}
      </ConsentCheckboxLabel>
    </ConsentCheckboxWrap>
  );
}

// ── radio-group ──────────────────────────────────────────────────────────────

const RadioGroupFieldset = styled.fieldset`
  border: none;
  margin: 0;
  padding: 0;
`;

const RadioGroupLegend = styled.legend<{ fontSize: number; color: string }>`
  font-size: ${({ fontSize }) => fontSize}px;
  font-weight: 600;
  color: ${({ color }) => color};
  margin-bottom: 8px;
  padding: 0;
`;

const RadioGroupOptions = styled.div<{ layout: string }>`
  display: flex;
  flex-direction: ${({ layout }) => (layout === 'horizontal' ? 'row' : 'column')};
  gap: ${({ layout }) => (layout === 'horizontal' ? '16px' : '8px')};
  flex-wrap: wrap;
`;

const RadioGroupOption = styled.label<{ fontSize: number; color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ fontSize }) => fontSize}px;
  color: ${({ color }) => color};
  cursor: pointer;
  user-select: none;
`;

const RadioGroupInput = styled.input`
  margin: 0;
  cursor: pointer;
  accent-color: #3b82f6;
`;

function RadioGroupBlock({ block }: { block: StepPageBlock }) {
  const label = str(block, 'label', 'Select an option');
  const rawOptions = str(block, 'options', 'Option A,Option B,Option C');
  const layout = str(block, 'layout', 'vertical') as 'vertical' | 'horizontal';
  const fontSize = num(block, 'fontSize', 14);
  const color = str(block, 'color', '#374151');

  const options = rawOptions.split(',').map((o) => o.trim()).filter(Boolean);
  const groupName = `radio-group-${block.id}`;

  return (
    <RadioGroupFieldset>
      <RadioGroupLegend fontSize={fontSize} color={color}>
        {label}
      </RadioGroupLegend>
      <RadioGroupOptions layout={layout}>
        {options.map((option, index) => {
          const optionId = `${groupName}-option-${index}`;
          return (
            <RadioGroupOption key={optionId} htmlFor={optionId} fontSize={fontSize} color={color}>
              <RadioGroupInput
                id={optionId}
                type="radio"
                name={groupName}
                value={option}
                aria-label={option}
                readOnly
              />
              {option}
            </RadioGroupOption>
          );
        })}
      </RadioGroupOptions>
    </RadioGroupFieldset>
  );
}

// ── leads-cta ───────────────────────────────────────────────────────────────

const LeadsCtaButton = styled.button<{
  bgColor: string;
  textColor: string;
  fontSize: number;
  borderRadius: number;
  fullWidth: boolean;
  hoverOpacity: number;
}>`
  display: ${({ fullWidth }) => (fullWidth ? 'block' : 'inline-block')};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  padding: 12px 24px;
  background-color: ${({ bgColor }) => bgColor};
  color: ${({ textColor }) => textColor};
  font-size: ${({ fontSize }) => fontSize}px;
  font-weight: 700;
  border: none;
  border-radius: ${({ borderRadius }) => borderRadius}px;
  cursor: pointer;
  letter-spacing: 0.02em;
  box-sizing: border-box;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: ${({ hoverOpacity }) => hoverOpacity};
  }
`;

function LeadsCta({ block, callbacks }: { block: StepPageBlock; callbacks?: BlockCallbacks }) {
  const text = str(block, 'text', 'Subscribe');
  const bgColor = str(block, 'bg_color', '#22c55e');
  const textColor = str(block, 'text_color', '#ffffff');
  const fontSize = num(block, 'fontSize', 16);
  const borderRadius = num(block, 'borderRadius', 8);
  const fullWidth = bool(block, 'fullWidth', true);
  const hoverOpacity = num(block, 'hoverOpacity', 0.9);

  return (
    <LeadsCtaButton
      type="button"
      bgColor={bgColor}
      textColor={textColor}
      fontSize={fontSize}
      borderRadius={borderRadius}
      fullWidth={fullWidth}
      hoverOpacity={hoverOpacity}
      onClick={callbacks?.onAccept}
    >
      {text}
    </LeadsCtaButton>
  );
}

// ---------------------------------------------------------------------------
// Landing Page Components
// ---------------------------------------------------------------------------

// ── lp-pricing-card ─────────────────────────────────────────────────────────

const PricingCardWrap = styled.div<{ bgColor: string; borderColor: string; highlighted: boolean }>`
  position: relative;
  border: 2px solid ${({ highlighted, borderColor }) => (highlighted ? '#3b82f6' : borderColor)};
  border-radius: 12px;
  padding: 24px 20px;
  background-color: ${({ bgColor }) => bgColor};
  box-shadow: ${({ highlighted }) => (highlighted ? '0 8px 24px rgba(59,130,246,0.18)' : 'none')};
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;
`;

const LpPricingBadge = styled.span`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: #3b82f6;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 3px 12px;
  border-radius: 100px;
  white-space: nowrap;
  letter-spacing: 0.03em;
`;

const PricingCardTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
`;

const PricingAmountRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
`;

const PricingAmount = styled.span`
  font-size: 36px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1;
`;

const PricingPeriod = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PricingFeatureList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PricingFeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`;

const LpCheckIcon = styled.span`
  color: #22c55e;
  font-size: 16px;
  flex-shrink: 0;
  line-height: 1.5;
`;

const PricingCtaButton = styled.button<{ highlighted: boolean }>`
  width: 100%;
  padding: 12px 0;
  background: ${({ highlighted }) => (highlighted ? '#3b82f6' : 'transparent')};
  color: ${({ highlighted }) => (highlighted ? '#fff' : '#3b82f6')};
  border: 2px solid #3b82f6;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: 0.02em;
  transition: opacity 0.15s ease;
  box-sizing: border-box;

  &:hover {
    opacity: 0.88;
  }
`;

function LpPricingCard({ block }: { block: StepPageBlock }) {
  const title = str(block, 'title', 'Starter Plan');
  const price = str(block, 'price', '$9.99');
  const period = str(block, 'period', '/month');
  const rawFeatures = str(block, 'features', 'Feature one,Feature two,Feature three');
  const highlighted = bool(block, 'highlighted', false);
  const bgColor = str(block, 'bg_color', '#ffffff');
  const borderColor = str(block, 'border_color', '#e5e7eb');
  const ctaText = str(block, 'cta_text', 'Get Started');
  const badge = str(block, 'badge', '');

  const features = rawFeatures.split(',').map((f) => f.trim()).filter(Boolean);

  return (
    <PricingCardWrap bgColor={bgColor} borderColor={borderColor} highlighted={highlighted}>
      {badge && <LpPricingBadge>{badge}</LpPricingBadge>}
      <PricingCardTitle>{title}</PricingCardTitle>
      <PricingAmountRow>
        <PricingAmount>{price}</PricingAmount>
        <PricingPeriod>{period}</PricingPeriod>
      </PricingAmountRow>
      <PricingFeatureList aria-label="Included features">
        {features.map((feature) => (
          <PricingFeatureItem key={feature}>
            <LpCheckIcon aria-hidden="true">✓</LpCheckIcon>
            {feature}
          </PricingFeatureItem>
        ))}
      </PricingFeatureList>
      <PricingCtaButton type="button" highlighted={highlighted} aria-label={ctaText}>
        {ctaText}
      </PricingCtaButton>
    </PricingCardWrap>
  );
}

// ── lp-feature-card ──────────────────────────────────────────────────────────

const FeatureCardWrap = styled.div<{ bgColor: string; borderRadius: number }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  background-color: ${({ bgColor }) => bgColor};
  border-radius: ${({ borderRadius }) => borderRadius}px;
  box-sizing: border-box;
`;

const FeatureIconCircle = styled.div<{ iconBgColor: string }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${({ iconBgColor }) => iconBgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  line-height: 1;
  flex-shrink: 0;
`;

const FeatureCardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FeatureCardDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

function LpFeatureCard({ block }: { block: StepPageBlock }) {
  const icon = str(block, 'icon', '⭐');
  const title = str(block, 'title', 'Feature Title');
  const description = str(block, 'description', 'Describe this feature here.');
  const bgColor = str(block, 'bg_color', '#f9fafb');
  const iconBgColor = str(block, 'icon_bg_color', '#eff6ff');
  const borderRadius = num(block, 'border_radius', 12);

  return (
    <FeatureCardWrap bgColor={bgColor} borderRadius={borderRadius}>
      <FeatureIconCircle iconBgColor={iconBgColor} aria-hidden="true">
        {icon}
      </FeatureIconCircle>
      <FeatureCardTitle>{title}</FeatureCardTitle>
      <FeatureCardDescription>{description}</FeatureCardDescription>
    </FeatureCardWrap>
  );
}

// ── lp-faq-item ──────────────────────────────────────────────────────────────

const FaqItemDetails = styled.details`
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};
  padding: 4px 0;

  &[open] summary .faq-chevron {
    transform: rotate(180deg);
  }
`;

const FaqItemSummary = styled.summary`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0;
  cursor: pointer;
  list-style: none;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  gap: 12px;

  &::-webkit-details-marker {
    display: none;
  }

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const FaqChevron = styled.span`
  display: inline-block;
  font-size: 14px;
  transition: transform 0.2s ease;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const LpFaqAnswer = styled.div`
  padding: 0 0 14px;
  font-size: 14px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

function LpFaqItem({ block }: { block: StepPageBlock }) {
  const question = str(block, 'question', 'Frequently asked question?');
  const answer = str(block, 'answer', 'Answer to the question goes here.');
  const open = bool(block, 'open', false);

  return (
    <FaqItemDetails open={open}>
      <FaqItemSummary aria-label={question}>
        {question}
        <FaqChevron className="faq-chevron" aria-hidden="true">▾</FaqChevron>
      </FaqItemSummary>
      <LpFaqAnswer>{answer}</LpFaqAnswer>
    </FaqItemDetails>
  );
}

// ── lp-accordion ─────────────────────────────────────────────────────────────

interface AccordionItem {
  question: string;
  answer: string;
}

function parseAccordionItems(raw: string): AccordionItem[] {
  // Try JSON array first: [{question, answer}, ...]
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .filter(
          (item): item is AccordionItem =>
            typeof item === 'object' &&
            item !== null &&
            typeof (item as Record<string, unknown>).question === 'string' &&
            typeof (item as Record<string, unknown>).answer === 'string',
        )
        .map((item) => ({ question: item.question, answer: item.answer }));
    }
  } catch {
    // fall through to pipe-comma format
  }

  // Fallback: "Q1|A1,Q2|A2" format
  return raw
    .split(',')
    .map((pair) => {
      const [question = '', answer = ''] = pair.split('|').map((s) => s.trim());
      return { question, answer };
    })
    .filter((item) => item.question);
}

const AccordionSection = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
`;

const AccordionHeading = styled.h2`
  margin: 0 0 12px;
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

function LpAccordion({ block }: { block: StepPageBlock }) {
  const title = str(block, 'title', 'FAQ');
  const rawItems = str(block, 'items', 'What is this?|This is an answer.,Can I return it?|Yes you can.');

  const items = parseAccordionItems(rawItems);

  return (
    <AccordionSection aria-label={title}>
      <AccordionHeading>{title}</AccordionHeading>
      {items.map((item, index) => {
        const faqBlock: StepPageBlock = {
          ...block,
          id: `${block.id}-faq-${index}`,
          type: 'lp-faq-item',
          props: {
            ...block.props,
            question: item.question,
            answer: item.answer,
            open: false,
          },
        };
        return <LpFaqItem key={index} block={faqBlock} />;
      })}
    </AccordionSection>
  );
}

// ── lp-stat-item ─────────────────────────────────────────────────────────────

const StatItemWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
  padding: 16px;
  box-sizing: border-box;
`;

const StatIcon = styled.span`
  font-size: 28px;
  line-height: 1;
`;

const StatValue = styled.span<{ valueColor: string; valueFontSize: number }>`
  display: block;
  font-size: ${({ valueFontSize }) => valueFontSize}px;
  font-weight: 800;
  color: ${({ valueColor }) => valueColor};
  line-height: 1.1;
`;

const LpStatLabel = styled.span`
  display: block;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 500;
`;

function LpStatItem({ block }: { block: StepPageBlock }) {
  const value = str(block, 'value', '10,000+');
  const label = str(block, 'label', 'Happy Customers');
  const icon = str(block, 'icon', '');
  const valueColor = str(block, 'value_color', '#3b82f6');
  const valueFontSize = num(block, 'value_font_size', 36);

  return (
    <StatItemWrap>
      {icon && <StatIcon aria-hidden="true">{icon}</StatIcon>}
      <StatValue valueColor={valueColor} valueFontSize={valueFontSize}>{value}</StatValue>
      <LpStatLabel>{label}</LpStatLabel>
    </StatItemWrap>
  );
}

// ---------------------------------------------------------------------------
// Required React import for JSX.IntrinsicElements usage
// ---------------------------------------------------------------------------
import React from 'react';

// ---------------------------------------------------------------------------
// video-player
// ---------------------------------------------------------------------------

const VideoWrapper = styled.div<{ borderRadius: number }>`
  width: 100%;
  border-radius: ${({ borderRadius }) => borderRadius}px;
  overflow: hidden;
  background: #000;
  line-height: 0;
`;

const VideoEl = styled.video`
  width: 100%;
  display: block;
`;

function VideoPlayer({ block }: { block: StepPageBlock }) {
  const src = str(block, 'src', '');
  const poster = str(block, 'poster', '');
  const autoplay = bool(block, 'autoplay', false);
  const muted = bool(block, 'muted', true);
  const loop = bool(block, 'loop', false);
  const controls = bool(block, 'controls', true);
  const width = str(block, 'width', '100%');
  const borderRadius = num(block, 'borderRadius', 8);

  if (!src) {
    return (
      <VideoWrapper borderRadius={borderRadius} style={{ width }} aria-label="Video player placeholder">
        <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
          No video source configured
        </div>
      </VideoWrapper>
    );
  }

  return (
    <VideoWrapper borderRadius={borderRadius} style={{ width }}>
      <VideoEl
        src={src}
        poster={poster || undefined}
        autoPlay={autoplay}
        muted={muted}
        loop={loop}
        controls={controls}
        playsInline
        aria-label="Offer video"
      />
    </VideoWrapper>
  );
}

// ---------------------------------------------------------------------------
// ticker-element
// ---------------------------------------------------------------------------

const TICKER_SPEEDS: Record<string, number> = {
  slow: 40,
  normal: 20,
  fast: 10,
};

const TickerOuter = styled.div<{ bgColor: string; padding: number }>`
  overflow: hidden;
  white-space: nowrap;
  background-color: ${({ bgColor }) => bgColor};
  padding: ${({ padding }) => padding}px 0;
`;

const TickerInner = styled.div<{ duration: number; direction: 'left' | 'right' }>`
  display: inline-block;
  white-space: nowrap;
  animation: tickerScroll ${({ duration }) => duration}s linear infinite;
  animation-direction: ${({ direction }) => (direction === 'right' ? 'reverse' : 'normal')};

  @keyframes tickerScroll {
    from { transform: translateX(0); }
    to { transform: translateX(-50%); }
  }
`;

const TickerSpan = styled.span<{ textColor: string; fontSize: number }>`
  display: inline-block;
  color: ${({ textColor }) => textColor};
  font-size: ${({ fontSize }) => fontSize}px;
  padding: 0 32px;
`;

function TickerElement({ block }: { block: StepPageBlock }) {
  const text = str(block, 'text', 'Special offer — Limited time only');
  const speed = str(block, 'speed', 'normal') as 'slow' | 'normal' | 'fast';
  const direction = str(block, 'direction', 'left') as 'left' | 'right';
  const bgColor = str(block, 'bg_color', '#1e293b');
  const textColor = str(block, 'text_color', '#fff');
  const fontSize = num(block, 'fontSize', 14);
  const padding = num(block, 'padding', 8);

  const duration = TICKER_SPEEDS[speed] ?? TICKER_SPEEDS.normal;
  // Duplicate the text so the scroll loops seamlessly
  const repeated = `${text}   •   ${text}   •   ${text}   •   ${text}   •   `;

  return (
    <TickerOuter bgColor={bgColor} padding={padding} role="marquee" aria-label={text}>
      <TickerInner duration={duration} direction={direction}>
        <TickerSpan textColor={textColor} fontSize={fontSize}>{repeated}</TickerSpan>
        <TickerSpan textColor={textColor} fontSize={fontSize} aria-hidden="true">{repeated}</TickerSpan>
      </TickerInner>
    </TickerOuter>
  );
}

// ---------------------------------------------------------------------------
// shape-element
// ---------------------------------------------------------------------------

type ShapeKind = 'rectangle' | 'circle' | 'line' | 'divider';

const ShapeDiv = styled.div<{
  shape: ShapeKind;
  width: string;
  height: string;
  bgColor: string;
  opacity: number;
  borderRadius: number;
}>`
  width: ${({ width }) => width};
  height: ${({ shape, height, width: w }) => (shape === 'circle' ? w : height)};
  background-color: ${({ bgColor }) => bgColor};
  opacity: ${({ opacity }) => opacity};
  border-radius: ${({ shape, borderRadius }) =>
    shape === 'circle' ? '50%' : `${borderRadius}px`};
  flex-shrink: 0;
`;

function ShapeElement({ block }: { block: StepPageBlock }) {
  const shape = str(block, 'shape', 'line') as ShapeKind;
  const width = str(block, 'width', '100%');
  const height = str(block, 'height', '2px');
  const bgColor = str(block, 'bg_color', '#e5e7eb');
  const opacity = num(block, 'opacity', 1);
  const borderRadius = num(block, 'borderRadius', 0);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}>
      <ShapeDiv
        shape={shape}
        width={width}
        height={height}
        bgColor={bgColor}
        opacity={opacity}
        borderRadius={borderRadius}
        aria-hidden="true"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Renders a Component Library block by its type ID.
 * Returns null for unknown types — the caller falls back to the legacy renderBlock().
 */
export interface BlockCallbacks {
  onAccept?: () => void;
  onDecline?: () => void;
  accepting?: boolean;
}

export function renderComponentBlock(block: StepPageBlock, offer: FunnelOffer, callbacks?: BlockCallbacks, offers?: FunnelOffer[]): React.ReactElement | null {
  switch (block.type) {
    // Tier 1
    case 'hero-banner':
    case 'hero_banner':
      return <HeroBanner key={block.id} block={block} offer={offer} />;
    case 'product-showcase':
    case 'product_showcase':
      return <ProductShowcase key={block.id} block={block} offer={offer} offers={offers} />;
    case 'testimonial-section':
    case 'testimonial_section':
    case 'testimonial':
      return <TestimonialSection key={block.id} block={block} />;
    case 'feature-grid':
    case 'feature_grid':
    case 'feature_list':
      return <FeatureGrid key={block.id} block={block} />;
    case 'pricing-table':
    case 'pricing_table':
    case 'price_display':
      return <PricingTable key={block.id} block={block} offer={offer} offers={offers} />;
    case 'faq-accordion':
    case 'faq_accordion':
      return <FaqAccordion key={block.id} block={block} />;
    case 'newsletter-signup':
    case 'newsletter_signup':
      return <NewsletterSignup key={block.id} block={block} />;
    case 'social-proof-bar':
    case 'social_proof_bar':
    case 'trust_badges':
      return <SocialProofBar key={block.id} block={block} />;

    // Interactive
    case 'spin-the-wheel':
    case 'spin_the_wheel':
      return <SpinTheWheel key={block.id} block={block} />;

    // Tier 2
    case 'image-text-block':
    case 'image_text_block':
      return <ImageTextBlock key={block.id} block={block} offer={offer} />;
    case 'cta-button-group':
    case 'cta_button_group':
    case 'cta_button':
    case 'cta':
      return <CtaButtonGroup key={block.id} block={block} offer={offer} callbacks={callbacks} />;
    case 'countdown-timer':
    case 'countdown_timer':
      return <CountdownTimer key={block.id} block={block} />;
    case 'progress-bar':
    case 'progress_bar':
      return <ProgressBar key={block.id} block={block} />;
    case 'video-embed':
    case 'video_embed':
      return <VideoEmbed key={block.id} block={block} />;
    case 'icon-list':
    case 'icon_list':
      return <IconList key={block.id} block={block} />;
    case 'stats-counter':
    case 'stats_counter':
      return <StatsCounter key={block.id} block={block} />;
    case 'comparison-table':
    case 'comparison_table':
      return <ComparisonTableBlock key={block.id} block={block} />;
    case 'badge-ribbon':
    case 'badge_ribbon':
    case 'badge':
      return <BadgeRibbon key={block.id} block={block} />;
    case 'divider':
      return <StyledDivider key={block.id} block={block} />;

    // Tier 3
    case 'heading':
    case 'headline':
      return (
        <div key={block.id} style={{ padding: '0 4px' }}>
          <HeadingBlock block={block} offer={offer} />
        </div>
      );
    case 'paragraph':
    case 'body_text':
      return (
        <div key={block.id} style={{ padding: '0 4px' }}>
          <ParagraphBlock block={block} offer={offer} />
        </div>
      );
    case 'image':
      return (
        <div key={block.id} style={{ display: 'flex', justifyContent: 'center' }}>
          <ImageBlock block={block} offer={offer} />
        </div>
      );
    case 'button':
      return (
        <div key={block.id} style={{ display: 'flex', justifyContent: 'center' }}>
          <ButtonBlock block={block} offer={offer} />
        </div>
      );
    case 'spacer': {
      const height = Math.max(8, num(block, 'height', 24));
      return <div key={block.id} style={{ height }} aria-hidden="true" />;
    }
    case 'container':
      return <ContainerWrapper key={block.id} block={block} />;
    case 'card':
      return <CardWrapper key={block.id} block={block} />;
    case 'input-field':
    case 'input_field':
      return (
        <div key={block.id} style={{ padding: '4px 8px' }}>
          <InputFieldBlock block={block} />
        </div>
      );
    case 'checkbox':
      return (
        <div key={block.id} style={{ padding: '4px 8px' }}>
          <CheckboxBlock block={block} />
        </div>
      );
    case 'select':
    case 'select-dropdown':
    case 'select_dropdown':
      return (
        <div key={block.id} style={{ padding: '4px 8px' }}>
          <SelectBlock block={block} />
        </div>
      );

    // Decline link (renders as secondary CTA)
    case 'decline_link':
    case 'decline-link':
      return (
        <CtaButtonGroup
          key={block.id}
          block={{
            ...block,
            props: {
              ...block.props,
              primary_text: '',
              secondary_text: str(block, 'text', offer.declineText),
              primary_color: 'transparent',
              primary_text_color: 'transparent',
            },
          }}
          offer={offer}
          callbacks={callbacks}
        />
      );

    // Guarantee badge (renders as badge with shield icon)
    case 'guarantee_badge':
    case 'guarantee-badge':
      return (
        <BadgeRibbon
          key={block.id}
          block={{
            ...block,
            props: {
              ...block.props,
              text: str(block, 'text', '') || str(block, 'label', 'Satisfaction Guaranteed'),
              bg_color: str(block, 'bg_color', '#dcfce7'),
              text_color: str(block, 'text_color', '#15803d'),
            },
          }}
        />
      );

    // Popup-specific components
    case 'popup-close-button':
      return <PopupCloseButton key={block.id} block={block} />;
    case 'popup-headline':
      return <PopupHeadline key={block.id} block={block} />;
    case 'discount-hero':
      return <DiscountHero key={block.id} block={block} />;
    case 'email-input':
      return (
        <div key={block.id} style={{ padding: '0 4px' }}>
          <EmailInputBlock block={block} />
        </div>
      );
    case 'leads-cta':
      return (
        <div key={block.id} style={{ padding: '0 4px' }}>
          <LeadsCta block={block} callbacks={callbacks} />
        </div>
      );

    // Form capture atoms
    case 'phone-input':
      return (
        <div key={block.id} style={{ padding: '0 4px' }}>
          <PhoneInputBlock block={block} />
        </div>
      );
    case 'name-input':
      return (
        <div key={block.id} style={{ padding: '0 4px' }}>
          <NameInputBlock block={block} />
        </div>
      );
    case 'address-input':
      return (
        <div key={block.id} style={{ padding: '0 4px' }}>
          <AddressInputBlock block={block} />
        </div>
      );
    case 'consent-checkbox':
      return (
        <div key={block.id} style={{ padding: '4px' }}>
          <ConsentCheckboxBlock block={block} />
        </div>
      );
    case 'radio-group':
      return (
        <div key={block.id} style={{ padding: '4px' }}>
          <RadioGroupBlock block={block} />
        </div>
      );

    // Landing page components
    case 'lp-pricing-card':
      return <LpPricingCard key={block.id} block={block} />;
    case 'lp-feature-card':
      return <LpFeatureCard key={block.id} block={block} />;
    case 'lp-faq-item':
      return <LpFaqItem key={block.id} block={block} />;
    case 'lp-accordion':
      return <LpAccordion key={block.id} block={block} />;
    case 'lp-stat-item':
      return <LpStatItem key={block.id} block={block} />;

    // New universal components
    case 'video-player':
      return <VideoPlayer key={block.id} block={block} />;
    case 'ticker-element':
      return <TickerElement key={block.id} block={block} />;
    case 'shape-element':
      return <ShapeElement key={block.id} block={block} />;

    default:
      return null;
  }
}
